import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageNavHeader from '../../components/layout/PageNavHeader';
import {
  PortfolioComparisonView,
  ComparisonResultsPageSkeleton,
  ComparisonCandlestickChart,
  assignComparisonColors,
  type ComparisonAccount,
  type PerformanceHistoryRange,
} from '@shared/PortfolioComparison';
import {
  RetailAccountContent,
  UserRetailAccount,
} from '@shared/types';
import {
  useRetailAccounts as useRetailAccountsQuery,
  useAccountContentQueries,
  useAccountHistoryQueries,
} from '../../queries/retailAccounts';
import ComparisonReturnChart from './ComparisonReturnChart';
import { Button } from '@/components/ui/button';

const QUERY_KEY = 'ids';

// Ranges that carry sub-daily (intraday) points and are fetched directly rather
// than sliced from the inception-anchored ALL series.
const INTRADAY_COMPARE_RANGES: PerformanceHistoryRange[] = ['1D', '1W'];

// Shimmer-free skeleton block: token-driven pulse on the muted surface.
const SK = 'animate-pulse rounded bg-muted';

const accountLabel = (
  account: UserRetailAccount,
  content?: RetailAccountContent,
) =>
  account.nickname ||
  content?.accountName ||
  (account.isPaper ? 'Paper Trading' : 'Live Trading');

const PortfolioComparisonPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState<PerformanceHistoryRange>('ALL');

  const idsParam = searchParams.get(QUERY_KEY) ?? '';
  const requestedIds = useMemo(
    () =>
      idsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    [idsParam],
  );
  const requestedIdSet = useMemo(() => new Set(requestedIds), [requestedIds]);

  const { data: accounts = [], isLoading: accountsLoading, error: accountsErr } = useRetailAccountsQuery();

  const accountsError = accountsErr
    ? ((accountsErr as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load portfolios')
    : null;

  const selectedAccounts = useMemo(() => {
    const byId = new Map(accounts.map((a) => [a.id, a]));
    return requestedIds
      .map((id) => byId.get(id))
      .filter((a): a is UserRetailAccount => Boolean(a));
  }, [accounts, requestedIds]);

  const missingIds = useMemo(
    () => requestedIds.filter((id) => !accounts.some((a) => a.id === id)),
    [accounts, requestedIds],
  );

  // Content queries — all enabled upfront since user explicitly picked these accounts
  const contentQueries = useAccountContentQueries(selectedAccounts, requestedIdSet);

  const contentById = useMemo(() => {
    const map: Record<string, RetailAccountContent | undefined> = {};
    selectedAccounts.forEach((acc, i) => { map[acc.id] = contentQueries[i]?.data; });
    return map;
  }, [selectedAccounts, contentQueries]);

  const contentStatusById = useMemo(() => {
    const map: Record<string, { loading: boolean; error: string | null }> = {};
    selectedAccounts.forEach((acc, i) => {
      const q = contentQueries[i];
      map[acc.id] = {
        loading: !!q?.isFetching,
        error: q?.error
          ? ((q.error as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load portfolio content')
          : null,
      };
    });
    return map;
  }, [selectedAccounts, contentQueries]);

  // History queries. Intraday ranges (1D/1W) re-fetch their own window so the
  // overlay shows sub-daily movement rebased to the window start (matches the
  // hero chart). Daily ranges (1M/ALL) fetch the inception-anchored ALL series
  // and only zoom the x-axis client-side (see ComparisonReturnChart). Only
  // enabled once content is available.
  const fetchRange: PerformanceHistoryRange = INTRADAY_COMPARE_RANGES.includes(
    selectedRange,
  )
    ? selectedRange
    : 'ALL';
  const historyQueries = useAccountHistoryQueries(selectedAccounts, contentById, fetchRange);

  const historyStatusById = useMemo(() => {
    const map: Record<string, { loading: boolean; error: string | null }> = {};
    selectedAccounts.forEach((acc, i) => {
      const q = historyQueries[i];
      map[acc.id] = {
        loading: !!q?.isFetching,
        error: q?.error
          ? ((q.error as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load performance history')
          : null,
      };
    });
    return map;
  }, [selectedAccounts, historyQueries]);

  // Range change: intraday ranges (1D/1W) refetch that window and re-base the
  // returns to its start; daily ranges (1M/ALL) only zoom the client-side x-axis
  // of the inception-anchored ALL series.
  const handleRangeChange = useCallback((range: PerformanceHistoryRange) => {
    setSelectedRange(range);
  }, []);

  const removeAccount = useCallback(
    (accountId: string) => {
      const remaining = requestedIds.filter((id) => id !== accountId);
      const params = new URLSearchParams(searchParams);
      if (remaining.length === 0) {
        params.delete(QUERY_KEY);
        navigate('/profile/portfolios/compare');
        return;
      }
      params.set(QUERY_KEY, remaining.join(','));
      setSearchParams(params, { replace: true });
    },
    [requestedIds, searchParams, setSearchParams, navigate],
  );

  const comparisonAccounts: ComparisonAccount[] = useMemo(() => {
    const colorById = assignComparisonColors(selectedAccounts);
    return selectedAccounts.map((account, i) => {
      const content = contentById[account.id];
      const contentStatus = contentStatusById[account.id];
      const historyStatus = historyStatusById[account.id];
      const historyData = historyQueries[i]?.data;
      return {
        accountId: account.id,
        label: accountLabel(account, content),
        color: colorById[account.id],
        account,
        content,
        contentLoading: contentStatus?.loading ?? true,
        contentError: contentStatus?.error ?? null,
        history: historyData,
        historyLoading: historyStatus?.loading ?? Boolean(content && !historyData),
        historyError: historyStatus?.error ?? null,
      };
    });
  }, [selectedAccounts, contentById, contentStatusById, historyStatusById, historyQueries]);

  if (accountsLoading) {
    return (
      <div className="mx-auto flex max-w-[1200px] flex-col gap-2 pb-12">
        <div className="flex flex-col gap-2.5 py-1" aria-hidden>
          <div className={`${SK} h-3.5 w-[min(420px,72%)]`} />
          <div className={`${SK} h-7 w-[min(320px,55%)]`} />
        </div>
        <ComparisonResultsPageSkeleton />
      </div>
    );
  }

  if (accountsError) {
    return (
      <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-3 pt-12 pb-12 text-center">
        <p className="text-[color:var(--nq-down-soft)]">{accountsError}</p>
        <Button
          variant="outline"
          onClick={() => {
            toast.dismiss();
            navigate('/profile/portfolios');
          }}
        >
          Back to portfolios
        </Button>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-3 pt-12 pb-12 text-center">
        <h2>You haven&apos;t linked any portfolios yet.</h2>
        <p>Add an account from the portfolios page first.</p>
        <Button variant="outline" asChild>
          <Link to="/profile/portfolios">Go to portfolios</Link>
        </Button>
      </div>
    );
  }

  if (selectedAccounts.length < 2) {
    return (
      <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-3 pt-12 pb-12 text-center">
        <h2>Select 2 or more portfolios to compare</h2>
        <p>Pick portfolios from the compare page and try again.</p>
        {missingIds.length > 0 && (
          <p className="text-[0.85rem] text-muted-foreground">
            Some portfolios in the URL are no longer available:{' '}
            {missingIds.join(', ')}
          </p>
        )}
        <Button variant="outline" asChild>
          <Link to="/profile/portfolios/compare">Go to compare</Link>
        </Button>
      </div>
    );
  }

  const anyHistoryLoading = comparisonAccounts.some((a) => a.historyLoading);
  const historyError =
    comparisonAccounts.find((a) => a.historyError)?.historyError ?? null;

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-2 pb-12">
      <PageNavHeader
        crumbs={[
          { label: 'Profile', to: '/profile' },
          { label: 'My Portfolios', to: '/profile/portfolios' },
          { label: 'Compare Portfolios', to: '/profile/portfolios/compare' },
          { label: 'Results' },
        ]}
        title={`Comparing ${comparisonAccounts.length} portfolios`}
        back={{
          to: '/profile/portfolios/compare',
          label: 'Back to compare',
        }}
      />
      <PortfolioComparisonView
        accounts={comparisonAccounts}
        hideTitle
        onRemoveAccount={removeAccount}
        statusMessage={
          missingIds.length > 0
            ? `Skipped ${missingIds.length} portfolio(s) no longer available.`
            : undefined
        }
        returnChart={
          <div className="flex flex-col gap-3">
            <ComparisonReturnChart
              accounts={comparisonAccounts}
              selectedRange={selectedRange}
              onRangeChange={handleRangeChange}
              loading={anyHistoryLoading}
              error={historyError}
            />
            <ComparisonCandlestickChart
              accounts={comparisonAccounts}
              selectedRange={selectedRange}
              onRangeChange={handleRangeChange}
              rangeOptions={['1D', '1W', '1M', 'ALL']}
              loading={anyHistoryLoading}
              error={historyError}
            />
          </div>
        }
      />
    </div>
  );
};

export default PortfolioComparisonPage;
